// src/pages/DiscoveryConfig/DiscoveryConfigListLayer.jsx
import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import {
  handleUnauthorized,
  showConfirmationDialog,
} from "../../utils/apiHelper";
import moment from "moment";
import {
  fetchDiscoveryConfigs,
  toggleDiscoveryConfigStatus,
  deleteDiscoveryConfig,
  deleteDiscoveryConfigBulk,
} from "../services/discoveryConfigService";
import { getModulePermissions } from "../../utils/permissionUtils";

const DiscoveryConfigListLayer = () => {
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFields, setTotalFields] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [statusFilter, setStatusFilter] = useState("");

  const navigate = useNavigate();
  const { shouldCheckPermissions, canEdit, canView, canDelete, canAdd } =
    getModulePermissions("discovery-config");

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: itemsPerPage,
        search: searchQuery,
        sortBy: sortColumn,
        sortOrder,
        status: statusFilter,
      };
      const res = await fetchDiscoveryConfigs(params);
      setItems(res.data || []);
      setTotalPages(res.totalPages || 1);
      setTotalFields(res.totalResults || 0);
    } catch (err) {
      if (err.status === 401) return handleUnauthorized(navigate);
      setErrorMessage(err.message || "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [
    currentPage,
    itemsPerPage,
    searchQuery,
    sortColumn,
    sortOrder,
    statusFilter,
  ]);

  const isSelected = (id) => selectedItems.includes(id);

  const handleSelectAll = (e) => {
    setSelectedItems(e.target.checked ? items.map((item) => item._id) : []);
  };

  const handleSelectItem = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleDelete = async ({ id = null, bulk = false }) => {
    let idsToDelete = bulk ? selectedItems : [id];
    if (!idsToDelete.length) return;

    const confirmMessage = bulk
      ? "This action will delete the selected Compatibility Algorithms!"
      : "This action will delete this Compatibility Algorithm!";
    const confirmBtnText = bulk ? "Delete All" : "Delete";
    const cancelBtnText = "Cancel";

    const result = await showConfirmationDialog(
      confirmMessage,
      confirmBtnText,
      cancelBtnText
    );
    if (!result.isConfirmed) return;

    try {
      if (bulk) {
        const res = await deleteDiscoveryConfigBulk(idsToDelete);
        setSuccessMessage(res.message || "Deleted successfully");
        setSelectedItems([]);
      } else {
        const res = await deleteDiscoveryConfig(idsToDelete[0]);
        setSuccessMessage(res.message || "Deleted successfully");
      }
      fetchData(currentPage);
    } catch (err) {
      setErrorMessage(err.message || "Delete failed");
    } finally {
      setTimeout(() => {
        setSuccessMessage("");
        setErrorMessage("");
      }, 3000);
    }
  };

  const updateStatus = async (id, currentStatus) => {
    const newStatus = !currentStatus;
    const result = await showConfirmationDialog(
      `Do you want to change status to ${newStatus ? "Active" : "Inactive"}?`
    );
    if (!result.isConfirmed) return;

    try {
      await toggleDiscoveryConfigStatus(id, { isActive: newStatus });
      setSuccessMessage(
        `Status updated to ${newStatus ? "Active" : "Inactive"}`
      );
      fetchData(currentPage);
    } catch (err) {
      setErrorMessage(err.message || "Failed to update status");
    } finally {
      setTimeout(() => {
        setSuccessMessage("");
        setErrorMessage("");
      }, 3000);
    }
  };

  const handleSort = (column) => {
    const newOrder =
      sortColumn === column && sortOrder === "asc" ? "desc" : "asc";
    setSortColumn(column);
    setSortOrder(newOrder);
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) setCurrentPage(pageNumber);
  };

  const getPaginationRange = () => {
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const handleNext = () => handlePageChange(currentPage + 1);
  const handlePrev = () => handlePageChange(currentPage - 1);

  return (
    <div className="card h-100 p-0 radius-12">
      {successMessage && <SuccessAlert message={successMessage} />}
      {errorMessage && <DengerAlert message={errorMessage} />}

      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light mb-0">
            Show
          </span>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(parseInt(e.target.value, 10));
              setCurrentPage(1);
            }}
          >
            <option value="10">10 items per page</option>
            <option value="20">20 items per page</option>
            <option value="50">50 items per page</option>
            <option value="100">100 items per page</option>
          </select>

          <form className="navbar-search" style={{ position: "relative" }}>
            <input
              type="text"
              className="bg-base h-40-px w-auto pe-40"
              name="search"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                paddingRight: searchQuery ? "5rem" : "2.5rem",
              }}
            />

            {/* Search Icon */}
            <Icon
              icon="ion:search-outline"
              className="icon"
              style={{
                position: "absolute",
                right: searchQuery ? "3.25rem" : "1rem",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "1.25rem",
                color: "#999",
                pointerEvents: "none",
              }}
            />

            {/* Clear (Close) Icon */}
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setCurrentPage(1);
                }}
                style={{
                  position: "absolute",
                  right: "1rem",
                  top: "30%",
                  transform: "translateY(-50%)",
                  fontSize: "1.25rem",
                  color: "#000",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </form>

          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          {/* <button
            className="btn btn-danger"
            onClick={() => handleDelete({ bulk: true })}
            disabled={selectedItems.length === 0}
          >
            Delete All
          </button> */}
        </div>
        {/* <Link
          to="/discovery-config/add-discovery-config"
          className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
        >
          <Icon
            icon="ic:baseline-plus"
            className="icon text-xl line-height-1"
          />
          Add Compatibility Algorithm
        </Link> */}
      </div>

      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th scope="col">
                  <div className="d-flex align-items-center gap-10">
                    <div className="form-check style-check d-flex align-items-center">
                      <input
                        className="form-check-input radius-4 border input-form-dark"
                        type="checkbox"
                        name="checkbox"
                        id="selectAll"
                        onChange={handleSelectAll}
                        checked={
                          selectedItems.length === items.length &&
                          items.length > 0
                        }
                      />
                    </div>
                  </div>
                </th>

                <th
                  onClick={() => handleSort("createdAt")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-0">
                    Created At
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${sortColumn === "createdAt" && sortOrder === "asc"
                          ? "active"
                          : ""
                          }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${sortColumn === "createdAt" && sortOrder === "desc"
                          ? "active"
                          : ""
                          }`}
                      />
                    </span>
                  </div>
                </th>
                {/* ✅ New Name column */}
                <th
                  onClick={() => handleSort("name")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-0">
                    Name
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${sortColumn === "name" && sortOrder === "asc"
                          ? "active"
                          : ""
                          }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${sortColumn === "name" && sortOrder === "desc"
                          ? "active"
                          : ""
                          }`}
                      />
                    </span>
                  </div>
                </th>
                <th>Weights</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center">
                    <span className="loader-15"></span>
                  </td>
                </tr>
              ) : items.length > 0 ? (
                items.map((item) => (
                  <tr key={item._id}>
                    <td>
                      <div className="d-flex align-items-center gap-10">
                        <div className="form-check style-check d-flex align-items-center">
                          <input
                            className="form-check-input radius-4 border border-neutral-400"
                            type="checkbox"
                            name="checkbox"
                            value={item._id}
                            checked={isSelected(item._id)}
                            onChange={() => handleSelectItem(item._id)}
                          />
                        </div>
                      </div>
                    </td>

                    <td>{moment(item.createdAt).format("DD-MM-YYYY")}</td>
                    <td>{item.name || "-"}</td>

                    <td>
                      Location Weight:{" "}
                      {`Near(${item.weights.distance.near}), Medium(${item.weights.distance.medium}), Far(${item.weights.distance.far}), VeryFar(${item.weights.distance.veryFar})`}{" "}
                      <br />
                      Recent Activity Weights:{" "}
                      {`Day(${item.weights.lastSeen.day}), 3Days(${item.weights.lastSeen.threeDays}), Week(${item.weights.lastSeen.week})`}{" "}
                      <br />
                      Matching Preferences: {item.weights.dynamicAnswers}
                      <br />
                      Gatekeeper Compatibility: {item.weights.gatekeeper}
                      <br />
                      Profile Completeness: {item.weights.profileSteps}
                      <br />
                     Connection Intent Compatibility:
                      {item.weights.connectionIntent}
                      <br />
                      Photos: {item.weights.photos}
                      <br />
                      Minimum Displayed Compatibility:{" "}
                      {item.minDisplayPercent ?? 75}%
                    </td>
                    <td>
                      {!shouldCheckPermissions || canEdit ? (
                        // Clickable span if user has permission
                        <span
                          onClick={() => updateStatus(item._id, item.isActive)}
                          className={`px-24 py-4 radius-4 fw-medium text-sm ${item.isActive
                            ? "bg-success-focus text-success-600 border border-success-main"
                            : "bg-neutral-200 text-neutral-600 border border-neutral-400"
                            }`}
                          style={{ cursor: "pointer" }}
                        >
                          {item.isActive ? "Active" : "Inactive"}
                        </span>
                      ) : (
                        // Non-clickable span if no permission
                        <span
                          className={`px-24 py-4 radius-4 fw-medium text-sm ${item.isActive
                            ? "bg-success-focus text-success-600 border border-success-main opacity-50"
                            : "bg-neutral-200 text-neutral-600 border border-neutral-400 opacity-50"
                            }`}
                          title="You don’t have permission to change status"
                          style={{ cursor: "default" }}
                        >
                          {item.isActive ? "Active" : "Inactive"}
                        </span>
                      )}
                    </td>

                    <td>
                      <div className="d-flex align-items-center gap-10">
                        {(!shouldCheckPermissions || canView) && (
                          <button
                            title="View"
                            onClick={() =>
                              navigate(
                                `/discovery-config/view-discovery-config/${item._id}`
                              )
                            }
                            className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="majesticons:eye-line" className="icon text-xl" />
                          </button>
                        )}
                        {(!shouldCheckPermissions || canEdit) && (
                          <button
                            title="Edit"
                            onClick={() =>
                              navigate(
                                `/discovery-config/edit-discovery-config/${item._id}`
                              )
                            }
                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                        )}
                        {(!shouldCheckPermissions || canDelete) && (
                          <button
                            title="Delete"
                            onClick={() => handleDelete({ id: item._id })}
                            className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="fluent:delete-24-regular" className="menu-icon"
                            />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center">
                    No Compatibility Algorithms found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <span>
            Showing{" "}
            {items?.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to{" "}
            {items?.length > 0
              ? Math.min(currentPage * itemsPerPage, totalFields)
              : 0}{" "}
            of {totalFields || 0} entries
          </span>

          <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
            {/* Previous Button */}
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                onClick={handlePrev}
                disabled={currentPage === 1}
              >
                <Icon icon="ep:d-arrow-left" />
              </button>
            </li>

            {/* Page Numbers */}
            {getPaginationRange().map((page) => (
              <li key={page} className="page-item">
                <button
                  className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${currentPage === page
                    ? "bg-primary-600 text-white"
                    : "bg-neutral-200"
                    }`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              </li>
            ))}

            {/* Next Button */}
            <li
              className={`page-item ${currentPage === totalPages ? "disabled" : ""
                }`}
            >
              <button
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                onClick={handleNext}
                disabled={currentPage === totalPages}
              >
                <Icon icon="ep:d-arrow-right" />
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DiscoveryConfigListLayer;
